// The day as Mükü's notebook page: hours down, danışmanlar across
// (day-view.md §6). Cells sit at their start and span their duration;
// overlaps share a column in lanes; cancelled sessions sit grey where
// they were; records with no plan behind them sit with the done ones.
// A cell says little (C, Onur 14.10.2026): the name, the time, money
// only when some is to be taken, and one round sign for the seans'
// state in the drawer's words (seansState). The whole cell takes the
// state's chip colour (Onur, same evening): done green, Kaydı yok
// yellow (its orange edge went 03.11.2026: the corner sign is enough),
// gelmiyor red, iptal grey; still to come stays white. A done seans
// whose fee is not fully paid keeps its green tick on a red cell (Onur,
// 15.10.2026). Everything else is in the drawer a tap opens.
//
// A cell can be dragged with the mouse up and down its own column,
// snapping to five minutes (§7): the drop hands the new time to the
// screen, which asks one line before anything is written. Dropping
// outside the column is a provider change, and that stays in chat.
// Touch keeps scrolling the day; a tablet moves a seans from the
// drawer's "Saati değiştir", which opens the same one-line confirm.

import {useCallback, useEffect, useRef, useState} from "react";
import type {
  CSSProperties, KeyboardEvent as ReactKeyboardEvent, PointerEvent as ReactPointerEvent,
} from "react";
import type {
  BoardCancelled, BoardCell, BoardClient, BoardProvider, BoardUnplanned, DayBoard,
} from "../api/contract";
import {
  CANCELLED_BY_TR, FLAG_TR, STATUS_TR, birthdayWords, moneyMark, seansState, waMark,
} from "../labels";
import {Avatar} from "../ui/Avatar";
import {CakeArt, CakeMark} from "../ui/Cake";
import {Icon, type IconName} from "../ui/Icon";
import {STATE_SIGN} from "./signs";
import {
  DEFAULT_SPAN_MIN, assignLanes, fromMin, gridWindow, hourLabel, hourOffset, shownProviders,
  snapStart, toMin,
} from "./layout";
import type {Placed, Span, Window} from "./layout";
import {useBlind} from "../ui/blind-context";

type Item =
  | {kind: "cell"; cell: BoardCell}
  | {kind: "cancelled"; rec: BoardCancelled}
  | {kind: "unplanned"; row: BoardUnplanned};

function spanOf(item: Item): Span | null {
  switch (item.kind) {
  case "cell":
    return {startMin: toMin(item.cell.time), endMin: toMin(item.cell.endTime)};
  case "cancelled": {
    if (!item.rec.time) return null;
    const start = toMin(item.rec.time);
    const end = item.rec.endTime ? toMin(item.rec.endTime) : start + DEFAULT_SPAN_MIN;
    return {startMin: start, endMin: Math.max(end, start + 10)};
  }
  case "unplanned": {
    if (!item.row.time) return null;
    const start = toMin(item.row.time);
    return {startMin: start, endMin: start + DEFAULT_SPAN_MIN};
  }
  }
}

const keyOf = (item: Item): string =>
  item.kind === "cell" ? `c:${item.cell.id}` :
    item.kind === "cancelled" ? `x:${item.rec.cancellationId}` : `u:${item.row.serviceId}`;

export interface GridProps {
  board: DayBoard;
  money: boolean; // the caller may see TL figures
  nowMin: number | null; // minutes into the day when the board is today
  today: string; // YYYY-MM-DD at the desk — a birthday is said from it
  // A tap on a cell / a cancelled slot. Absent = the grid is read-only.
  onSelectCell?: (cell: BoardCell) => void;
  onSelectCancelled?: (rec: BoardCancelled) => void;
  // A drop after a drag: the new start (HH:MM), or null when the cell
  // was let go outside its own column. Absent = nothing drags.
  onMove?: (cell: BoardCell, time: string | null) => void;
  // A tap on a danışman's name: her drawer. Absent = the head reads.
  onSelectProvider?: (providerId: string) => void;
}

export function Grid({board, money, nowMin, today, onSelectCell, onSelectCancelled, onMove,
  onSelectProvider}: GridProps) {
  const items: (Span & {item: Item; providerId: string})[] = [];
  const push = (providerId: string, item: Item) => {
    const s = spanOf(item);
    if (s && s.endMin > s.startMin) items.push({...s, item, providerId});
  };
  for (const c of board.cells) push(c.providerId, {kind: "cell", cell: c});
  for (const r of board.cancelled) push(r.providerId, {kind: "cancelled", rec: r});
  for (const u of board.unplanned) push(u.providerId, {kind: "unplanned", row: u});

  const w = gridWindow(board.hours, items);
  const hours = (w.toMin - w.fromMin) / 60;
  const labels: number[] = [];
  for (let m = w.fromMin; m < w.toMin; m += 60) labels.push(m);

  // Columns only for the danışmanlar with something on the day.
  const providers = shownProviders(board.providers, items.map((i) => i.providerId));
  if (providers.length === 0) {
    return <p className="muted">Bu gün için planlı seans yok.</p>;
  }
  const style = {"--hours": hours, "--cols": providers.length} as CSSProperties;
  const showNow = nowMin !== null && nowMin >= w.fromMin && nowMin <= w.toMin;
  const nameOf = new Map(providers.map((p) => [p.providerId, p.name]));
  // A seans still on the calendar under a standing cancellation is not
  // for moving — its removal is what is pending.
  const movable = (c: BoardCell) => c.status !== "cancelled" && c.status !== "paused";

  return (
    <div className={`grid${providers.length > 1 ? " multi" : ""}`} style={style} role="table" aria-label={`${board.date} günü`}>
      <div className="grid-head" role="row">
        <div className="gutter" />
        {providers.map((p) => (
          <ColHead key={p.providerId} provider={p}
            count={board.cells.filter((c) => c.providerId === p.providerId &&
              c.status !== "cancelled").length}
            onSelect={onSelectProvider ? () => onSelectProvider(p.providerId) : undefined} />
        ))}
      </div>
      <div className="grid-body">
        <div className="gutter">
          {labels.map((m) => (
            <span key={m} className="hour-label"
              style={{top: `calc(var(--hour-h) * ${hourOffset(w, m)})`}}>
              {hourLabel(m)}
            </span>
          ))}
        </div>
        {providers.map((p) => {
          const mine = items.filter((i) => i.providerId === p.providerId);
          const placed = assignLanes(mine.map(({item, startMin, endMin}) =>
            ({item, startMin, endMin})));
          return (
            <div key={p.providerId} className={`col${p.calendarOk ? "" : " col-dead"}`}
              role="cell">
              {placed.map((pl) => {
                const c = pl.item.kind === "cell" ? pl.item.cell : null;
                const r = pl.item.kind === "cancelled" ? pl.item.rec : null;
                return (
                  <GridCell key={keyOf(pl.item)} placed={pl} w={w} money={money} today={today}
                    providerName={nameOf.get(p.providerId) ?? ""}
                    onSelect={c && onSelectCell ? () => onSelectCell(c) :
                      r && onSelectCancelled ? () => onSelectCancelled(r) : undefined}
                    onMove={c && onMove && movable(c) ? (time) => onMove(c, time) : undefined} />
                );
              })}
            </div>
          );
        })}
        {showNow && (
          <div className="now-line" aria-hidden
            style={{top: `calc(var(--hour-h) * ${hourOffset(w, nowMin)})`}} />
        )}
      </div>
    </div>
  );
}

// A column's head: her name and how many seanslar she has, or why the
// column is empty. A tap opens her drawer.
function ColHead({provider: p, count, onSelect}: {
  provider: BoardProvider; count: number; onSelect?: () => void;
}) {
  const inner = <>
    <Avatar providerId={p.providerId} name={p.name} photo={p.photo} />
    <span className="col-name">{p.name}</span>
    {p.calendarOk ?
      <span className="col-count" title="seans">{count}</span> :
      <span className="chip bad">takvim okunamadı</span>}
  </>;
  return (
    <div className="col-head" role="columnheader">
      {onSelect ?
        <button className="col-btn" onClick={onSelect} title={`${p.name}: günü ve hesabı`}>
          {inner}<Icon name="chev" />
        </button> : inner}
    </div>
  );
}

// ── Dragging ──────────────────────────────────────────────

interface DragState {
  startMin: number; // where the cell would land, snapped
  outside: boolean; // the pointer left the column
}

// Mouse only: touch keeps scrolling the day (a tablet moves a seans
// from the drawer). The cell is lifted after six pixels, follows the
// pointer in five-minute steps inside the window, and on release
// hands over the new time — or null when let go outside its column.
// A click that ends a drag is not a tap. Escape drops it back.
function useCellDrag(o: {
  startMin: number; endMin: number; w: Window;
  onMove?: (time: string | null) => void;
}) {
  const live = useRef<{
    startX: number; startY: number; pxPerMin: number;
    colLeft: number; colRight: number;
    moved: boolean; target: number; outside: boolean;
  } | null>(null);
  const swallowClick = useRef(false);
  const [state, setState] = useState<DragState | null>(null);
  const {onMove, startMin} = o;

  const end = useCallback((commit: boolean) => {
    const d = live.current;
    live.current = null;
    setState(null);
    if (!d?.moved) return;
    swallowClick.current = true;
    if (!commit) return;
    if (d.outside) onMove?.(null);
    else if (d.target !== startMin) onMove?.(fromMin(d.target));
  }, [onMove, startMin]);

  const active = state !== null;
  useEffect(() => {
    if (!active) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") end(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active, end]);

  const handlers = !onMove ? {} : {
    onPointerDown: (e: ReactPointerEvent<HTMLDivElement>) => {
      swallowClick.current = false;
      if (e.button !== 0 || e.pointerType !== "mouse") return;
      const col = e.currentTarget.parentElement;
      if (!col) return;
      const r = col.getBoundingClientRect();
      live.current = {
        startX: e.clientX, startY: e.clientY,
        pxPerMin: r.height / (o.w.toMin - o.w.fromMin),
        colLeft: r.left, colRight: r.right,
        moved: false, target: startMin, outside: false,
      };
      e.currentTarget.setPointerCapture(e.pointerId);
    },
    onPointerMove: (e: ReactPointerEvent<HTMLDivElement>) => {
      const d = live.current;
      if (!d) return;
      const dy = e.clientY - d.startY;
      if (!d.moved && Math.abs(dy) < 6 && Math.abs(e.clientX - d.startX) < 6) return;
      d.moved = true;
      d.target = snapStart(startMin, o.endMin - startMin, dy / d.pxPerMin, o.w);
      d.outside = e.clientX < d.colLeft || e.clientX > d.colRight;
      setState({startMin: d.target, outside: d.outside});
    },
    onPointerUp: () => end(true),
    onPointerCancel: () => end(false),
  };

  const ignoreClick = () => {
    const s = swallowClick.current;
    swallowClick.current = false;
    return s;
  };
  return {state, handlers, ignoreClick};
}

// ── One cell ──────────────────────────────────────────────

interface CellProps {
  placed: Placed<Item>;
  w: Window;
  money: boolean;
  today: string;
  providerName: string;
  onSelect?: () => void;
  onMove?: (time: string | null) => void;
}

// A tappable cell: a button for the keyboard and the screen reader,
// the same div for the layout. `ignore` lets a drag's release not
// count as a tap.
function tappable(onSelect?: () => void, ignore?: () => boolean) {
  if (!onSelect) return {};
  return {
    role: "button" as const,
    tabIndex: 0,
    onClick: () => {
      if (ignore?.()) return;
      onSelect();
    },
    onKeyDown: (e: ReactKeyboardEvent) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onSelect();
      }
    },
  };
}

function GridCell({placed, w, money, today, providerName, onSelect, onMove}: CellProps) {
  const {item, startMin, endMin, lane, lanes} = placed;
  const drag = useCellDrag({startMin, endMin, w, onMove});
  const blind = useBlind();
  const shownStart = drag.state?.startMin ?? startMin;
  const pos: CSSProperties = {
    top: `calc(var(--hour-h) * ${hourOffset(w, shownStart)})`,
    height: `calc(var(--hour-h) * ${(endMin - startMin) / 60})`,
    left: `calc(${(lane / lanes) * 100}% + 3px)`,
    width: `calc(${(1 / lanes) * 100}% - 6px)`,
  };
  const narrow = lanes > 1;
  const short = endMin - startMin < 30;
  const classes = ["cell", narrow ? "narrow" : "", short ? "short" : "",
    onSelect ? "tappable" : ""];

  if (item.kind === "cancelled") {
    return <CancelledCell rec={item.rec} pos={pos} classes={classes} onSelect={onSelect} />;
  }
  if (item.kind === "unplanned") return <UnplannedCell row={item.row} pos={pos} classes={classes} />;

  const c = item.cell;
  const source = c.clients.find((x) => x.attendance.state === "confirmed")?.attendance.source ?? null;
  const state = seansState(c.status, c.flags, source);
  const names = c.clients.map((x) => x.name).join(", ") || c.title;
  const title = [
    `${c.time}–${c.endTime} ${names} — ${providerName}`,
    state.word,
    ...c.flags.map((f) => FLAG_TR[f].word),
    ...c.clients.flatMap((x) => clientTitle(x, money, blind, today)),
  ].join(" · ");
  const dragging = drag.state;
  const due = money ? c.clients.map((x) => moneyMark(x.money, blind))
    .filter((m) => m?.kind === "collect") : [];
  const marks = c.clients.flatMap((x) => clientMarks(x));
  const sign = STATE_SIGN[state.kind];
  // A birthday within a week either side (Onur, 23.10.2026): a seans
  // that is still on wears the birthday's indigo and its cake, the round
  // sign still saying where it stands; one that is off keeps a small
  // cake by its marks. Until 03.11.2026 only the day itself wore the
  // indigo and the week had the small cake — Onur: "hitting the exact
  // birthday for a client at the session date is odd".
  const off = state.kind === "cancelled" || state.kind === "paused" || state.kind === "declined";
  const party = !off && c.clients.some((x) => x.birthday);
  const cakes = off ? c.clients.flatMap((x) => x.birthday ?
    [{x, words: birthdayWords(x.birthday, today)}] : []) : [];

  return (
    <div className={[...classes, `st-${state.kind}`, c.status === "recorded" ? "unpaid" : "",
      party ? "bday" : "", dragging ? "dragging" : "", dragging?.outside ? "outside" : ""]
      .join(" ")}
    style={pos} title={title} aria-label={title}
    {...tappable(onSelect, drag.ignoreClick)} {...drag.handlers}>
      <span className="gc-who">{names}</span>
      <span className="gc-sub">
        <span className="time">{dragging ? fromMin(dragging.startMin) : c.time}</span>
        {due.map((m, i) => m && (
          <span key={i} className="gc-due" title={m.title}>{m.text.replace(" alınacak", " TL")}</span>
        ))}
        {c.clients.length === 0 && <span className="gc-warn">{FLAG_TR.unresolved_client.word}</span>}
        {c.cancellation && <span className="gc-warn">{FLAG_TR.still_on_calendar.word}</span>}
        {marks.map((m, i) => (
          <span key={i} className={`gc-mark ${m.tone}`} title={m.title}><Icon name={m.icon} /></span>
        ))}
        {cakes.map(({x, words}) => (
          <span key={`cake-${x.clientId}`} className="gc-mark gc-cake" title={`${x.name}: ${words}`}>
            <CakeMark />
          </span>
        ))}
      </span>
      {party && <CakeArt />}
      <span className={`gc-sign st-${state.kind}`} aria-hidden>
        {sign && <Icon name={sign} />}
      </span>
      {dragging?.outside && <span className="drag-hint">danışman değişikliği sohbetten</span>}
    </div>
  );
}

// The few marks that still sit in the cell, each a line icon: a form
// owed (the drawer's pen), a desk note (its pin), a WhatsApp answer to
// read or a message that did not go. Everything else is the drawer's.
interface Mark { icon: IconName; tone: string; title: string }
function clientMarks(x: BoardClient): Mark[] {
  const out: Mark[] = [];
  if (x.sozlesme.mark !== "ok") out.push({icon: "note", tone: "warn", title: `${x.name}: ${x.sozlesme.text ?? "sözleşme"}`});
  if (x.deskNote) out.push({icon: "pin", tone: "", title: `${x.name}: ${x.deskNote}`});
  if (x.attendance.state === "text" || x.attendance.state === "failed") {
    const wa = waMark(x.attendance.state, x.attendance.source);
    out.push({icon: "msg", tone: x.attendance.state === "failed" ? "bad" : "blue",
      title: `${x.name}: ${wa?.word ?? "WhatsApp"}`});
  }
  return out;
}

function clientTitle(x: BoardClient, money: boolean, blind: boolean, today: string): string[] {
  const out: string[] = [];
  if (x.birthday) out.push(`${x.name}: ${birthdayWords(x.birthday, today)}`);
  if (x.sozlesme.text) out.push(`${x.name}: ${x.sozlesme.text}`);
  const wa = waMark(x.attendance.state, x.attendance.source);
  if (wa) out.push(`${x.name}: ${wa.word}`);
  if (x.deskNote) out.push(`${x.name}: ${x.deskNote}`);
  const m = money ? moneyMark(x.money, blind) : null;
  if (m) out.push(`${x.name}: ${m.title}`);
  if (x.recorded) out.push(`${x.name}: ${x.recorded.covered ? "ödendi" : "kaydedildi"}`);
  return out;
}

function CancelledCell({rec: r, pos, classes, onSelect}: {rec: BoardCancelled;
  pos: CSSProperties; classes: string[]; onSelect?: () => void}) {
  const sign = r.kind === "paused" ? STATUS_TR.paused : STATUS_TR.cancelled;
  const who = CANCELLED_BY_TR[r.cancelledBy] ?? r.cancelledBy;
  const names = r.clientNames.join(", ") || "—";
  const title = `${r.time}–${r.endTime ?? "?"} ${names} · ${sign.word} · ${who}: ${r.reason}`;
  return (
    <div className={[...classes, "st-cancelled"].join(" ")} style={pos} title={title}
      aria-label={title} {...tappable(onSelect)}>
      <span className="gc-who">{names}</span>
      <span className="gc-sub">
        <span className="time">{r.time}</span>
        <span className="gc-reason">{r.kind === "paused" ? "ara · " : ""}{r.reason}</span>
      </span>
    </div>
  );
}

function UnplannedCell({row: u, pos, classes}: {row: BoardUnplanned; pos: CSSProperties;
  classes: string[]}) {
  const sign = u.recorded.covered ? STATUS_TR.paid : STATUS_TR.recorded;
  const title = `${u.time} ${u.name} · plansız kayıt · ${sign.word}`;
  return (
    <div className={[...classes, "st-done", "unplanned-cell", u.recorded.covered ? "" : "unpaid"]
      .join(" ")} style={pos} title={title}
      aria-label={title}>
      <span className="gc-who">{u.name}</span>
      <span className="gc-sub"><span className="time">{u.time}</span> plansız</span>
      <span className="gc-sign st-done" aria-hidden><Icon name="check" /></span>
    </div>
  );
}
